import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'sonner'
import {
  ArrowLeft, ArrowRight, MapPin, Calendar, Euro, FileText,
  CheckCircle2, AlertCircle, Clock, Zap, Camera,
  Paintbrush, Building2, Hammer, Home, Shield, Key, Grid3X3, Snowflake,
  Tag, Wrench, ChevronRight, Mail, RefreshCw, Loader2, Lock,
  Paperclip, Image as ImageIcon, X,
} from 'lucide-react'
import { Turnstile } from '@repo/ui/turnstile'
import { HoneypotField } from '@repo/ui/honeypot-field'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import PublicPageHeader from '../components/PublicPageHeader'
import PublicFooter from '../components/PublicFooter'
import { useAuth } from '../context/AuthContext'
import { authService, uploadService, postsService } from '../services/api'
import { cn } from '../lib/utils'

import API from '../constants/api'
// API base via getApiBase()

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

/** Map common category names to icons (fallback: Tag) */
const CATEGORY_ICON_MAP = {
  plomberie: Wrench,
  électricité: Zap,
  electricite: Zap,
  peinture: Paintbrush,
  maçonnerie: Building2,
  maconnerie: Building2,
  menuiserie: Hammer,
  rénovation: Home,
  renovation: Home,
  toiture: Shield,
  serrurerie: Key,
  carrelage: Grid3X3,
  climatisation: Snowflake,
  photographie: Camera,
}

const getCategoryIcon = (name = '') => {
  const key = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return CATEGORY_ICON_MAP[key] ?? Tag
}

const normalizeText = (text = '') =>
  text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()

const findAutreCategory = (categoryList) =>
  categoryList.find((cat) => normalizeText(cat.name) === 'autre') ?? null

const URGENCY_OPTIONS = [
  { value: 'normale', label: 'Normal', description: 'Sous 2-3 semaines', icon: Clock, color: 'bg-slate-100 text-slate-700' },
  { value: 'urgente', label: 'Urgent', description: 'Sous 1 semaine', icon: AlertCircle, color: 'bg-amber-100 text-amber-700' },
  { value: 'tres_urgente', label: 'Très urgent', description: 'Sous 48h', icon: Zap, color: 'bg-red-100 text-red-700' },
]

const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
const ATTACHMENT_MAX_COUNT = 5
const ATTACHMENT_ALLOWED_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/webp',
  'image/gif',
]

const formatAttachmentBytes = (size) => {
  if (size < 1024) return `${size} o`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} Ko`
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`
}

const inferAttachmentMime = (name, fallback = 'application/octet-stream') => {
  const ext = name.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') return 'application/pdf'
  if (ext === 'png') return 'image/png'
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
  if (ext === 'webp') return 'image/webp'
  if (ext === 'gif') return 'image/gif'
  return fallback
}

const validateAttachmentFile = (file) => {
  if (file.size > ATTACHMENT_MAX_BYTES) return 'Le fichier ne doit pas dépasser 10 Mo'
  const mime = file.type || inferAttachmentMime(file.name)
  if (!ATTACHMENT_ALLOWED_MIMES.includes(mime)) {
    return 'Format non supporté. Utilisez PDF, JPG, PNG, WebP ou GIF.'
  }
  return null
}

export default function CreateRequest() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isAuthenticated, getAuthToken, refreshUser } = useAuth()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  const [authSubStep, setAuthSubStep] = useState('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [debugCode, setDebugCode] = useState('')
  const [authError, setAuthError] = useState('')
  const [authSubmitting, setAuthSubmitting] = useState(false)
  const [pendingPublish, setPendingPublish] = useState(false)
  const [website, setWebsite] = useState('') // honeypot — real users never fill this
  const [sendCaptchaToken, setSendCaptchaToken] = useState('')
  const [resendCaptchaToken, setResendCaptchaToken] = useState('')
  const [resendWidgetKey, setResendWidgetKey] = useState(0)

  const codeInputRefs = useRef([])
  const searchParamsApplied = useRef(false)
  const attachmentInputRef = useRef(null)

  const [attachments, setAttachments] = useState([])

  const isLoggedIn = isAuthenticated && Boolean(user)
  const totalSteps = isLoggedIn ? 3 : 4

  const [formData, setFormData] = useState({
    categoryId: '',
    categoryName: '',
    title: '',
    description: '',
    locationCity: '',
    budgetMin: '',
    budgetMax: '',
    urgence: 'normale',
    dateSouhaitee: '',
  })

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`)
        const data = res.data?.data ?? res.data?.categories ?? res.data ?? []
        setCategories(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('[CreateRequest] categories fetch error:', err)
        toast.error('Impossible de charger les catégories')
      } finally {
        setCategoriesLoading(false)
      }
    }
    fetchCategories()
  }, [])

  useEffect(() => {
    if (searchParamsApplied.current) return

    const titre = searchParams.get('titre')?.trim() || ''
    const localisation = searchParams.get('localisation')?.trim() || ''

    if (!titre && !localisation) return
    if (categoriesLoading) return

    searchParamsApplied.current = true

    const autreCategory = findAutreCategory(categories)
    const updates = {
      ...(titre && { title: titre }),
      ...(localisation && { locationCity: localisation }),
    }

    if (autreCategory) {
      updates.categoryId = autreCategory.id
      updates.categoryName = autreCategory.name
    } else {
      updates.categoryName = 'Autre'
    }

    setStep(2)
    setFormData((prev) => ({ ...prev, ...updates }))
  }, [searchParams, categoriesLoading, categories])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  useEffect(() => {
    if (step === 4 && authSubStep === 'code' && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus()
    }
  }, [step, authSubStep])

  const handleCategorySelect = (cat) => {
    setFormData({ ...formData, categoryId: cat.id, categoryName: cat.name })
    setStep(2)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleAttachmentPick = (e) => {
    const pickedFiles = Array.from(e.target.files ?? [])
    if (!pickedFiles.length) return

    if (attachments.length + pickedFiles.length > ATTACHMENT_MAX_COUNT) {
      toast.error(`Vous pouvez ajouter au maximum ${ATTACHMENT_MAX_COUNT} pièces jointes`)
      e.target.value = ''
      return
    }

    const nextAttachments = [...attachments]

    for (const file of pickedFiles) {
      const error = validateAttachmentFile(file)
      if (error) {
        toast.error(`${file.name} : ${error}`)
        continue
      }

      nextAttachments.push({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
      })
    }

    setAttachments(nextAttachments)
    e.target.value = ''
  }

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId))
  }

  const uploadAttachments = async () => {
    if (!attachments.length) return []

    const uploaded = []

    for (const attachment of attachments) {
      const uploadRes = await uploadService.uploadFile(attachment.file)
      const url = uploadRes?.data?.url ?? uploadRes?.url
      if (!url) {
        throw new Error(`Impossible d'uploader ${attachment.file.name}`)
      }

      uploaded.push({
        url,
        name: attachment.file.name,
        mimeType: attachment.file.type || inferAttachmentMime(attachment.file.name),
        size: attachment.file.size,
      })
    }

    return uploaded
  }

  const validateStep2 = () => {
    if (!formData.title.trim() || formData.title.length < 5) {
      toast.error('Le titre doit contenir au moins 5 caractères')
      return false
    }
    if (!formData.locationCity.trim()) {
      toast.error('Veuillez indiquer votre localisation')
      return false
    }
    return true
  }

  const handleNext = () => {
    if (step === 2 && !validateStep2()) return
    setStep(step + 1)
  }

  const handleBack = () => setStep(step - 1)

  const handleSendCode = async () => {
    if (!email.trim() || !email.includes('@')) {
      setAuthError('Veuillez entrer une adresse email valide')
      return
    }
    if (TURNSTILE_SITE_KEY && !sendCaptchaToken) {
      setAuthError('Merci de valider la vérification anti-robot')
      return
    }

    setAuthSubmitting(true)
    setAuthError('')
    try {
      const response = await authService.sendCode(email.trim(), { captchaToken: sendCaptchaToken, website })
      if (response.debug_code) {
        setDebugCode(response.debug_code)
      }
      setAuthSubStep('code')
      setResendCooldown(60)
      setCode(['', '', '', '', '', ''])
    } catch (error) {
      setAuthError(error.message || 'Erreur lors de l\'envoi du code')
    } finally {
      setAuthSubmitting(false)
    }
  }

  const handleVerifyCode = async (fullCode = code.join('')) => {
    if (fullCode.length !== 6) {
      setAuthError('Veuillez entrer le code à 6 chiffres')
      return
    }

    setAuthSubmitting(true)
    setAuthError('')
    try {
      await authService.verifyCode(email.trim(), fullCode)
      const userData = await refreshUser()
      if (!userData) {
        setAuthError('Connexion réussie mais impossible de charger votre profil. Réessayez.')
        return
      }
      await handleSubmit(pendingPublish)
    } catch (error) {
      setAuthError(error.message || 'Code incorrect')
      setCode(['', '', '', '', '', ''])
      codeInputRefs.current[0]?.focus()
    } finally {
      setAuthSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0 || authSubmitting) return
    if (TURNSTILE_SITE_KEY && !resendCaptchaToken) {
      setAuthError('Merci de valider la vérification anti-robot')
      return
    }

    setAuthSubmitting(true)
    setAuthError('')
    try {
      const response = await authService.resendCode(email.trim(), { captchaToken: resendCaptchaToken, website })
      if (response.debug_code) {
        setDebugCode(response.debug_code)
      }
      setResendCooldown(60)
      setCode(['', '', '', '', '', ''])
      codeInputRefs.current[0]?.focus()
    } catch (error) {
      setAuthError(error.message || 'Erreur lors du renvoi')
    } finally {
      setAuthSubmitting(false)
      // Turnstile tokens are single-use — remount the widget for a fresh one.
      setResendCaptchaToken('')
      setResendWidgetKey((k) => k + 1)
    }
  }

  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)

    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus()
    }

    const fullCode = newCode.join('')
    if (fullCode.length === 6) {
      void handleVerifyCode(fullCode)
    }
  }

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      setCode(pastedData.split(''))
      void handleVerifyCode(pastedData)
    }
  }

  const handleAuthSubmit = async (event) => {
    event?.preventDefault()
    if (authSubStep === 'email') {
      await handleSendCode()
    } else {
      await handleVerifyCode()
    }
  }

  const handleAuthBack = () => {
    setAuthError('')
    if (authSubStep === 'code') {
      setAuthSubStep('email')
      setCode(['', '', '', '', '', ''])
      setDebugCode('')
      return
    }
    setStep(3)
  }

  const handlePublishClick = (publish = false) => {
    if (!isLoggedIn) {
      setPendingPublish(publish)
      setAuthSubStep('email')
      setAuthError('')
      setStep(4)
      return
    }
    handleSubmit(publish)
  }

  const handleSubmit = async (publish = false) => {
    const authToken = getAuthToken()
    if (!authToken) {
      handlePublishClick(publish)
      return
    }

    setLoading(true)
    try {
      const uploadedAttachments = await uploadAttachments()

      // Encode budget + urgence into the salaryRange JSON string
      const salaryRangeParts = {}
      if (formData.budgetMin) salaryRangeParts.min = Number(formData.budgetMin)
      if (formData.budgetMax) salaryRangeParts.max = Number(formData.budgetMax)
      if (formData.urgence && formData.urgence !== 'normale') salaryRangeParts.urgence = formData.urgence
      if (formData.dateSouhaitee) salaryRangeParts.dateSouhaitee = formData.dateSouhaitee

      const payload = {
        type: 'REQUEST',
        title: formData.title,
        description: formData.description,
        categoryId: formData.categoryId,
        locationCity: formData.locationCity,
        status: publish ? 'PUBLISHED' : 'DRAFT',
        ...(uploadedAttachments.length > 0 && { attachments: uploadedAttachments }),
        ...(Object.keys(salaryRangeParts).length > 0 && {
          salaryRange: JSON.stringify(salaryRangeParts),
        }),
      }

      await postsService.createPost(payload)

      toast.success(publish ? 'Votre demande a été publiée !' : 'Brouillon enregistré')
      navigate('/tableau-de-bord/mes-demandes')
    } catch (error) {
      console.error('[CreateRequest] submit error:', error)
      const msg =
        error.response?.data?.error?.message ??
        error.response?.data?.message ??
        'Erreur lors de la création'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const selectedUrgency = URGENCY_OPTIONS.find((u) => u.value === formData.urgence)

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicPageHeader activePath="demandes" />

      <div className="bg-white border-b border-slate-100">
        <div className="container-clickpro py-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <a href="https://clicketpro.fr" className="hover:text-[hsl(var(--primary))]">Accueil</a>
            <ChevronRight className="w-4 h-4" />
            <Link to="/demandes" className="hover:text-[hsl(var(--primary))]">Demandes</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium">Déposer une demande</span>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.08)] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Déposer une demande</h1>
          </div>
          <p className="text-slate-600">
            Décrivez votre projet et recevez des réponses de professionnels qualifiés
          </p>
        </div>
      </div>

      <main className="container-clickpro py-8 max-w-4xl">

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Étape {Math.min(step, totalSteps)} sur {totalSteps}</span>
              <span className="text-sm text-slate-500">
                {step === 1 && 'Choisissez une catégorie'}
                {step === 2 && 'Décrivez votre projet'}
                {step === 3 && 'Finalisez votre demande'}
                {step === 4 && 'Connectez-vous pour publier'}
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--primary))] transition-all duration-500 ease-out"
                style={{ width: `${(Math.min(step, totalSteps) / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Step 1: Category Selection */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="step-1">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Quel type de travaux avez-vous besoin ?
                </h2>
                <p className="text-lg text-slate-600">
                  Sélectionnez la catégorie qui correspond le mieux à votre projet
                </p>
              </div>

              {categoriesLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="h-28 bg-white rounded-xl border border-slate-200 animate-pulse" />
                  ))}
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  Aucune catégorie disponible pour le moment.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {categories.map((cat) => {
                    const IconComponent = getCategoryIcon(cat.name)
                    const isSelected = formData.categoryId === cat.id
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat)}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 text-left hover:shadow-md ${
                          isSelected
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] shadow-md'
                            : 'border-slate-200 bg-white hover:border-[hsl(var(--primary)/0.4)]'
                        }`}
                        data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                          style={{ backgroundColor: cat.color ? `${cat.color}20` : undefined }}
                        >
                          <IconComponent
                            className="w-6 h-6"
                            style={{ color: cat.color ?? (isSelected ? 'hsl(var(--primary))' : '#475569') }}
                          />
                        </div>
                        <h3 className={`font-semibold mb-1 text-sm ${isSelected ? 'text-[hsl(var(--primary-hover))]' : 'text-slate-900'}`}>
                          {cat.name}
                        </h3>
                      </button>
                    )
                  })}
                </div>
              )}

              <div className="mt-8 text-center">
                <Link to="/demandes" className="text-[hsl(var(--primary))] hover:text-[hsl(var(--primary-hover))] text-sm font-medium">
                  Voir les demandes existantes
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: Project Details */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="step-2">
              <div className="mb-8">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Décrivez votre projet
                </h2>
                <Badge className="bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary-hover))]">{formData.categoryName}</Badge>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-6">
                <div>
                  <Label htmlFor="title" className="text-base font-medium">
                    Titre de votre demande *
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">Soyez précis pour attirer les bons artisans</p>
                  <Input
                    id="title"
                    name="title"
                    placeholder="Ex: Installation d'un chauffe-eau thermodynamique"
                    value={formData.title}
                    onChange={handleChange}
                    className="text-base"
                    data-testid="input-titre"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-base font-medium">
                    Description détaillée
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">Plus vous êtes précis, meilleurs seront les devis</p>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Décrivez votre projet en détail : dimensions, matériaux souhaités, contraintes particulières..."
                    value={formData.description}
                    onChange={handleChange}
                    rows={5}
                    className="text-base resize-none"
                    data-testid="input-description"
                  />
                  <p className="text-xs text-slate-400 mt-1">{formData.description.length}/2000 caractères</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Pièces jointes
                    </Label>
                    <span className="text-xs text-slate-500">Optionnel · {attachments.length}/{ATTACHMENT_MAX_COUNT}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Photos ou documents utiles (PDF, JPG, PNG, WebP, GIF · 10 Mo max)
                  </p>

                  {attachments.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {attachments.map((attachment) => {
                        const isPdf = attachment.file.type === 'application/pdf'
                          || attachment.file.name.toLowerCase().endsWith('.pdf')

                        return (
                          <div
                            key={attachment.id}
                            className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50"
                          >
                            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.08)] flex items-center justify-center shrink-0">
                              {isPdf ? (
                                <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
                              ) : (
                                <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{attachment.file.name}</p>
                              <p className="text-xs text-slate-500">{formatAttachmentBytes(attachment.file.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 hover:bg-slate-100"
                              aria-label={`Supprimer ${attachment.file.name}`}
                            >
                              <X className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {attachments.length < ATTACHMENT_MAX_COUNT && (
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid="btn-add-attachment"
                    >
                      <Paperclip className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <span className="text-sm font-medium text-slate-700">
                        {attachments.length ? 'Ajouter une autre pièce jointe' : 'Ajouter une pièce jointe'}
                      </span>
                    </button>
                  )}

                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleAttachmentPick}
                    data-testid="input-attachments"
                  />
                </div>

                <div>
                  <Label htmlFor="locationCity" className="text-base font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Localisation *
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">Ville ou code postal</p>
                  <Input
                    id="locationCity"
                    name="locationCity"
                    placeholder="Ex: Paris 75015, Lyon, Marseille..."
                    value={formData.locationCity}
                    onChange={handleChange}
                    className="text-base"
                    data-testid="input-localisation"
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleNext} size="lg" className="btn-clickpro-primary" data-testid="btn-next-step2">
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Budget & Urgency */}
          {step === 3 && (
            <div className="animate-fade-in" data-testid="step-3">
              <div className="mb-8">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Finalisez votre demande
                </h2>
                <p className="text-slate-600">Budget et délais souhaités</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-8">

                {/* Budget */}
                <div>
                  <Label className="text-base font-medium flex items-center gap-2 mb-4">
                    <Euro className="w-4 h-4" />
                    Budget estimé (optionnel)
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="budgetMin" className="text-sm text-slate-500">Minimum (€)</Label>
                      <Input
                        id="budgetMin"
                        name="budgetMin"
                        type="number"
                        min="0"
                        placeholder="500"
                        value={formData.budgetMin}
                        onChange={handleChange}
                        data-testid="input-budget-min"
                      />
                    </div>
                    <div>
                      <Label htmlFor="budgetMax" className="text-sm text-slate-500">Maximum (€)</Label>
                      <Input
                        id="budgetMax"
                        name="budgetMax"
                        type="number"
                        min="0"
                        placeholder="2000"
                        value={formData.budgetMax}
                        onChange={handleChange}
                        data-testid="input-budget-max"
                      />
                    </div>
                  </div>
                </div>

                {/* Urgency */}
                <div>
                  <Label className="text-base font-medium flex items-center gap-2 mb-4">
                    <Clock className="w-4 h-4" />
                    Niveau d'urgence
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {URGENCY_OPTIONS.map((option) => {
                      const IconComponent = option.icon
                      const isSelected = formData.urgence === option.value
                      return (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, urgence: option.value })}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                            isSelected
                              ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)]'
                              : 'border-slate-200 bg-white hover:border-[hsl(var(--primary)/0.4)]'
                          }`}
                          data-testid={`urgency-${option.value}`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${option.color}`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <h4 className="font-semibold text-slate-900">{option.label}</h4>
                          <p className="text-sm text-slate-500">{option.description}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Desired date */}
                <div>
                  <Label htmlFor="dateSouhaitee" className="text-base font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date souhaitée (optionnel)
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">Quand souhaitez-vous que les travaux débutent ?</p>
                  <Input
                    id="dateSouhaitee"
                    name="dateSouhaitee"
                    type="date"
                    value={formData.dateSouhaitee}
                    onChange={handleChange}
                    className="max-w-xs"
                    data-testid="input-date"
                  />
                </div>

                {/* Summary */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Récapitulatif
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Catégorie</span>
                      <span className="font-medium text-slate-900">{formData.categoryName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Titre</span>
                      <span className="font-medium text-slate-900 text-right max-w-[60%] truncate">{formData.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Localisation</span>
                      <span className="font-medium text-slate-900">{formData.locationCity}</span>
                    </div>
                    {attachments.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Pièces jointes</span>
                        <span className="font-medium text-slate-900">
                          {attachments.length} fichier{attachments.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Urgence</span>
                      <Badge className={selectedUrgency?.color}>{selectedUrgency?.label}</Badge>
                    </div>
                    {(formData.budgetMin || formData.budgetMax) && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Budget</span>
                        <span className="font-medium text-slate-900">
                          {formData.budgetMin && `${formData.budgetMin}€`}
                          {formData.budgetMin && formData.budgetMax && ' – '}
                          {formData.budgetMax && `${formData.budgetMax}€`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  {!isLoggedIn && (
                    <div className="bg-[hsl(var(--primary)/0.06)] border border-[hsl(var(--primary)/0.2)] rounded-lg p-4 flex-1">
                      <p className="text-[hsl(var(--primary-hover))] text-sm">
                        <Lock className="w-4 h-4 inline mr-2" />
                        Une dernière étape : connectez-vous pour publier votre demande. Aucun mot de passe requis.
                      </p>
                    </div>
                  )}
                  <div className="flex gap-3 sm:ml-auto">
                    <Button
                      variant="outline"
                      onClick={() => handlePublishClick(false)}
                      disabled={loading || authSubmitting}
                      data-testid="btn-save-draft"
                    >
                      Enregistrer brouillon
                    </Button>
                    <Button
                      onClick={() => handlePublishClick(true)}
                      disabled={loading || authSubmitting}
                      className="btn-clickpro-primary"
                      data-testid="btn-publish"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Publier ma demande
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Authentication */}
          {step === 4 && (
            <div className="animate-fade-in" data-testid="step-4">
              <div className="mb-8">
                <button
                  onClick={handleAuthBack}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                {authSubStep === 'email' ? (
                  <>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                      Connectez-vous pour {pendingPublish ? 'publier' : 'enregistrer'}
                    </h2>
                    <p className="text-slate-600">
                      Entrez votre email pour recevoir un code de connexion. Aucun mot de passe requis.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                      Vérification
                    </h2>
                    <p className="text-slate-600">
                      Entrez le code envoyé à{' '}
                      <span className="font-medium text-slate-900">{email}</span>
                    </p>
                  </>
                )}
              </div>

              <form onSubmit={handleAuthSubmit}>
                <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-6">
                  {authSubStep === 'email' ? (
                    <div className="space-y-2">
                      <Label htmlFor="request-auth-email">Adresse e-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="request-auth-email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="exemple@email.com"
                          className="pl-10 text-base"
                          data-testid="request-login-email"
                        />
                      </div>

                      <HoneypotField value={website} onChange={setWebsite} />

                      {TURNSTILE_SITE_KEY && (
                        <Turnstile
                          siteKey={TURNSTILE_SITE_KEY}
                          onVerify={setSendCaptchaToken}
                          onExpire={() => setSendCaptchaToken('')}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {debugCode && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                          <p className="text-amber-800 text-sm">
                            <strong>Mode développement</strong> — Code :{' '}
                            <span className="font-mono font-bold">{debugCode}</span>
                          </p>
                        </div>
                      )}

                      <div
                        className="flex justify-center gap-2"
                        onPaste={handleCodePaste}
                      >
                        {code.map((digit, index) => (
                          <Input
                            key={index}
                            ref={(el) => { codeInputRefs.current[index] = el }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleCodeKeyDown(index, e)}
                            className="w-10 h-12 text-center text-xl font-bold px-0"
                            data-testid={`request-code-input-${index}`}
                          />
                        ))}
                      </div>

                      {TURNSTILE_SITE_KEY && (
                        <div className="flex justify-center">
                          <Turnstile
                            key={resendWidgetKey}
                            siteKey={TURNSTILE_SITE_KEY}
                            onVerify={setResendCaptchaToken}
                            onExpire={() => setResendCaptchaToken('')}
                          />
                        </div>
                      )}

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={resendCooldown > 0 || authSubmitting || (Boolean(TURNSTILE_SITE_KEY) && !resendCaptchaToken)}
                          className={cn(
                            'inline-flex items-center gap-2 text-sm',
                            resendCooldown > 0
                              ? 'text-slate-400 cursor-not-allowed'
                              : 'text-[hsl(var(--primary))] hover:underline'
                          )}
                          data-testid="request-resend-code"
                        >
                          <RefreshCw className="w-4 h-4" />
                          {resendCooldown > 0
                            ? `Renvoyer le code (${resendCooldown}s)`
                            : 'Renvoyer le code'}
                        </button>
                      </div>
                    </>
                  )}

                  {authError && (
                    <p
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                      role="alert"
                    >
                      {authError}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <p className="flex items-center gap-1 text-xs text-slate-400">
                      <Lock className="w-3 h-3" />
                      Données sécurisées
                    </p>
                    <Button
                      type="submit"
                      className="btn-clickpro-primary"
                      disabled={
                        authSubmitting
                        || (authSubStep === 'code' && code.join('').length !== 6)
                        || (authSubStep === 'email' && Boolean(TURNSTILE_SITE_KEY) && !sendCaptchaToken)
                      }
                      data-testid="request-auth-submit"
                    >
                      {authSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : authSubStep === 'code' ? (
                        <>
                          {pendingPublish ? 'Publier ma demande' : 'Enregistrer brouillon'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          Recevoir un code
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Trust badges */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3 text-center md:text-left justify-center md:justify-start">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">Service gratuit</h4>
                <p className="text-xs text-slate-500">Déposez votre demande sans frais</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-center md:text-left justify-center md:justify-start">
              <div className="w-10 h-10 bg-[hsl(var(--primary)/0.08)] rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">Artisans vérifiés</h4>
                <p className="text-xs text-slate-500">Profils validés et certifiés</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-center md:text-left justify-center md:justify-start">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">Réponses rapides</h4>
                <p className="text-xs text-slate-500">Devis en moins de 48h</p>
              </div>
            </div>
          </div>
      </main>
      <PublicFooter />
    </div>
  )
}
